import { useEffect, useState, useRef, useCallback } from 'react';
import { type PriceHistory } from '@/mocks/priceData';

interface PriceChartProps {
  data: PriceHistory[];
  enabled: boolean;
  currencySymbol?: string;
  darkMode?: boolean;
  locale?: string;
  currency?: string;
}

export function PriceChart({
  data,
  enabled,
  currencySymbol,
  darkMode = false,
  locale = 'pt-BR',
  currency = 'BRL',
}: PriceChartProps) {
  const [animated, setAnimated] = useState(false);
  const [lineDrawn, setLineDrawn] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const mobileScrollRef = useRef<HTMLDivElement>(null);
  const touchStartX = useRef<number>(0);
  const touchStartY = useRef<number>(0);

  useEffect(() => {
    if (enabled) {
      const t1 = setTimeout(() => setAnimated(true), 200);
      const t2 = setTimeout(() => setLineDrawn(true), 1200);
      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
      };
    }
    setAnimated(false);
    setLineDrawn(false);
    setSelectedIndex(null);
  }, [enabled]);

  const maxValue = Math.max(...data.map((d) => d.pricePerSqm));
  const minValue = Math.min(...data.map((d) => d.pricePerSqm));

  const getBarHeight = (value: number) => {
    const heightPercent = ((value - minValue * 0.85) / (maxValue * 1.15 - minValue * 0.85)) * 100;
    return Math.max(12, Math.min(100, heightPercent));
  };

  // SVG line points
  const points = data.map((item, index) => {
    const x = ((index + 0.5) / data.length) * 100;
    const barHeight = getBarHeight(item.pricePerSqm);
    const y = 82 - (barHeight * 0.62);
    return { x, y, value: item.pricePerSqm };
  });

  const pathD = points.length > 1
    ? `M ${points[0].x} ${points[0].y} ` + points.slice(1).map((p) => `L ${p.x} ${p.y}`).join(' ')
    : '';

  const effectiveSymbol = currencySymbol ?? (
    currency === 'USD' ? '$' : 'R$'
  );

  const formatCompact = (value: number) => {
    if (value >= 1000) {
      return `${effectiveSymbol} ${(value / 1000).toFixed(1).replace('.', ',')}k`;
    }
    return `${effectiveSymbol} ${value}`;
  };

  const formatFull = (value: number) =>
    value.toLocaleString(locale, {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    });

  // Touch swipe handlers
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  }, []);

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      const deltaX = e.changedTouches[0].clientX - touchStartX.current;
      const deltaY = e.changedTouches[0].clientY - touchStartY.current;

      // Only handle horizontal swipes (horizontal movement > vertical)
      if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 30) {
        setSelectedIndex((prev) => {
          if (prev === null) {
            return deltaX < 0 ? 0 : data.length - 1;
          }
          const next = deltaX < 0 ? Math.min(prev + 1, data.length - 1) : Math.max(prev - 1, 0);
          return next;
        });
      }
    },
    [data.length],
  );

  // Auto-scroll to selected bar on mobile
  useEffect(() => {
    if (selectedIndex !== null && mobileScrollRef.current) {
      const container = mobileScrollRef.current;
      const barEl = container.children[selectedIndex] as HTMLElement | undefined;
      if (barEl) {
        barEl.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center',
        });
      }
    }
  }, [selectedIndex]);

  const selectedData = selectedIndex !== null ? data[selectedIndex] : null;
  const prevData = selectedIndex !== null && selectedIndex > 0 ? data[selectedIndex - 1] : null;

  return (
    <div className="w-full">
      {/* ============================================== */}
      {/* DESKTOP CHART (md and up) — hover tooltips     */}
      {/* ============================================== */}
      <div className="hidden md:block">
        <div className="flex items-end justify-between gap-2 md:gap-4 h-52 md:h-64 relative">
          {/* Grid lines */}
          <svg
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            className={`absolute inset-0 w-full h-full z-20 pointer-events-none transition-opacity duration-500 ${lineDrawn ? 'opacity-100' : 'opacity-0'}`}
          >
            <path
              d={pathD}
              fill="none"
              stroke={darkMode ? 'rgba(94,234,212,0.15)' : 'rgba(20,184,166,0.12)'}
              strokeWidth="0.6"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="transition-all duration-1000"
              style={{
                filter: darkMode ? 'drop-shadow(0 2px 4px rgba(94,234,212,0.3))' : 'drop-shadow(0 2px 4px rgba(20,184,166,0.2))',
              }}
            />
            <path
              d={pathD}
              fill="none"
              stroke={darkMode ? '#5eead4' : '#14b8a6'}
              strokeWidth="0.35"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray="200"
              strokeDashoffset={lineDrawn ? '0' : '200'}
              className="transition-all duration-[1500ms] ease-out"
            />
            {points.map((p, i) => (
              <g key={i}>
                <circle
                  cx={p.x}
                  cy={p.y}
                  r="1.8"
                  fill={darkMode ? 'rgba(94,234,212,0.2)' : 'rgba(20,184,166,0.15)'}
                  className="transition-all duration-500"
                  style={{ transitionDelay: `${1500 + i * 200}ms`, opacity: lineDrawn ? 1 : 0 }}
                />
                <circle
                  cx={p.x}
                  cy={p.y}
                  r="0.9"
                  fill={darkMode ? '#0d9488' : '#0f766e'}
                  stroke={darkMode ? '#5eead4' : '#14b8a6'}
                  strokeWidth="0.25"
                  className="transition-all duration-500"
                  style={{ transitionDelay: `${1600 + i * 200}ms`, opacity: lineDrawn ? 1 : 0 }}
                />
              </g>
            ))}
          </svg>

          {/* Dashed grid */}
          <div className={`absolute inset-0 flex flex-col justify-between pointer-events-none z-0 ${darkMode ? 'opacity-20' : ''}`}>
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className={`w-full border-t border-dashed ${darkMode ? 'border-white/20' : 'border-gray-100'}`} />
            ))}
          </div>

          {data.map((item, index) => {
            const clampedHeight = getBarHeight(item.pricePerSqm);
            const isLatest = index === data.length - 1;
            const isSelected = selectedIndex === index;

            return (
              <div
                key={item.year}
                className={`flex-1 flex flex-col items-center justify-end relative z-10 group cursor-pointer transition-all duration-200 ${
                  isSelected ? 'scale-[1.03]' : ''
                }`}
                onClick={() => setSelectedIndex(selectedIndex === index ? null : index)}
              >
                <div
                  className={`mb-1.5 text-xs md:text-sm font-bold tabular-nums transition-all duration-700 ${
                    animated ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'
                  } ${isLatest
                    ? (darkMode ? 'text-teal-300' : 'text-teal-700')
                    : (darkMode ? 'text-white/50' : 'text-gray-400')
                  } ${
                    isSelected && !isLatest ? (darkMode ? '!text-white/90' : '!text-gray-700') : ''
                  }`}
                  style={{ transitionDelay: `${index * 150 + 400}ms` }}
                >
                  {formatCompact(item.pricePerSqm)}
                </div>

                <div
                  className={`w-full max-w-[56px] md:max-w-[72px] rounded-t-xl transition-all duration-1000 ease-out relative overflow-hidden ${
                    isLatest
                      ? 'bg-gradient-to-t from-teal-600 to-emerald-500'
                      : isSelected
                        ? (darkMode
                          ? 'bg-gradient-to-t from-white/30 to-white/15'
                          : 'bg-gradient-to-t from-teal-400 to-teal-300')
                        : (darkMode
                          ? 'bg-gradient-to-t from-white/15 to-white/5'
                          : 'bg-gradient-to-t from-gray-200 to-gray-100')
                  }`}
                  style={{
                    height: animated ? `${clampedHeight}%` : '0%',
                    transitionDelay: `${index * 200}ms`,
                  }}
                >
                  {isLatest && (
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] animate-shine" />
                  )}
                </div>

                <div
                  className={`mt-2 text-xs font-semibold transition-all duration-500 ${
                    animated ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-1'
                  } ${isLatest
                    ? (darkMode ? 'text-teal-300' : 'text-teal-700')
                    : (darkMode ? 'text-white/40' : 'text-gray-400')
                  } ${
                    isSelected && !isLatest ? (darkMode ? '!text-white/90' : '!text-gray-700') : ''
                  }`}
                  style={{ transitionDelay: `${index * 150 + 600}ms` }}
                >
                  {item.label}
                </div>

                {/* Hover tooltip */}
                <div className="absolute bottom-full mb-8 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-30">
                  <div className="bg-gray-900 text-white text-xs px-3 py-2 rounded-lg whitespace-nowrap shadow-lg">
                    {formatFull(item.pricePerSqm)} /m²
                    <div className="absolute top-full left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-900 rotate-45" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ============================================== */}
      {/* MOBILE CHART (below md) — touch optimized      */}
      {/* ============================================== */}
      <div
        className="md:hidden"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {/* Bar container — horizontally scrollable */}
        <div
          ref={mobileScrollRef}
          className="flex items-end gap-3 h-52 relative overflow-x-auto hide-scrollbar px-2 pb-2"
        >
          {/* Grid lines */}
          <div className={`absolute inset-x-2 inset-y-0 flex flex-col justify-between pointer-events-none z-0 ${darkMode ? 'opacity-15' : ''}`}>
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className={`w-full border-t border-dashed ${darkMode ? 'border-white/20' : 'border-gray-100'}`} />
            ))}
          </div>

          {data.map((item, index) => {
            const clampedHeight = getBarHeight(item.pricePerSqm);
            const isLatest = index === data.length - 1;
            const isSelected = selectedIndex === index;

            return (
              <div
                key={item.year}
                className={`flex-shrink-0 flex flex-col items-center justify-end relative z-10 cursor-pointer transition-all duration-200 active:scale-95 ${
                  isSelected ? 'scale-[1.05]' : ''
                }`}
                style={{ width: '48px' }}
                onClick={() => setSelectedIndex(selectedIndex === index ? null : index)}
              >
                {/* Value label */}
                <div
                  className={`mb-1.5 text-xs font-bold tabular-nums transition-all duration-700 whitespace-nowrap ${
                    animated ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'
                  } ${
                    isSelected
                      ? (darkMode ? 'text-teal-300' : 'text-teal-700')
                      : isLatest
                        ? (darkMode ? 'text-teal-300' : 'text-teal-700')
                        : (darkMode ? 'text-white/40' : 'text-gray-400')
                  }`}
                  style={{ transitionDelay: `${index * 150 + 400}ms` }}
                >
                  {formatCompact(item.pricePerSqm)}
                </div>

                {/* Bar */}
                <div
                  className={`w-full rounded-t-xl transition-all duration-1000 ease-out relative overflow-hidden ${
                    isSelected
                      ? 'bg-gradient-to-t from-teal-600 to-emerald-500'
                      : isLatest
                        ? 'bg-gradient-to-t from-teal-600 to-emerald-500'
                        : (darkMode
                          ? 'bg-gradient-to-t from-white/12 to-white/4'
                          : 'bg-gradient-to-t from-gray-200 to-gray-100')
                  }`}
                  style={{
                    height: animated ? `${clampedHeight}%` : '0%',
                    transitionDelay: `${index * 200}ms`,
                  }}
                >
                  {isLatest && (
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/15 to-transparent translate-x-[-100%] animate-shine" />
                  )}
                </div>

                {/* Year label */}
                <div
                  className={`mt-2 text-xs font-semibold transition-all duration-500 whitespace-nowrap ${
                    animated ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-1'
                  } ${
                    isSelected
                      ? (darkMode ? 'text-teal-300' : 'text-teal-700')
                      : isLatest
                        ? (darkMode ? 'text-teal-300' : 'text-teal-700')
                        : (darkMode ? 'text-white/30' : 'text-gray-400')
                  }`}
                  style={{ transitionDelay: `${index * 150 + 600}ms` }}
                >
                  {item.label}
                </div>

                {/* Selected indicator dot */}
                {isSelected && (
                  <div className="absolute -bottom-1 w-2 h-2 rounded-full bg-teal-500 shadow-sm" />
                )}
              </div>
            );
          })}
        </div>

        {/* Swipe hint */}
        {selectedIndex === null && (
          <div className="mt-2 flex items-center justify-center gap-1">
            <span className={`text-[10px] ${darkMode ? 'text-white/30' : 'text-gray-400'}`}>
              Deslize ou toque nas barras
            </span>
          </div>
        )}

        {/* Detail panel — shows when a bar is selected */}
        <div
          className={`mt-3 overflow-hidden transition-all duration-300 ${
            selectedData
              ? 'max-h-32 opacity-100 translate-y-0'
              : 'max-h-0 opacity-0 translate-y-2'
          }`}
        >
          {selectedData && (
            <div className={`rounded-xl p-4 ${
              darkMode
                ? 'bg-white/10 border border-white/10'
                : 'bg-teal-50/70 border border-teal-100'
            }`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${darkMode ? 'bg-teal-400' : 'bg-teal-500'}`} />
                  <span className={`text-sm font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {selectedData.label}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedIndex(null)}
                  className={`w-6 h-6 rounded-full flex items-center justify-center text-xs cursor-pointer transition-colors ${
                    darkMode ? 'text-white/40 hover:text-white/80 hover:bg-white/10' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <i className="ri-close-line"></i>
                </button>
              </div>
              <div className="flex items-baseline gap-4">
                <span className={`text-2xl font-bold tabular-nums ${
                  darkMode ? 'text-teal-300' : 'text-teal-700'
                }`} style={{ fontFamily: "'Playfair Display', serif" }}>
                  {formatFull(selectedData.pricePerSqm)}
                </span>
                <span className={`text-sm ${darkMode ? 'text-white/50' : 'text-gray-500'}`}>
                  /m²
                </span>
              </div>
              {prevData && (
                <div className={`mt-2 flex items-center gap-1.5 text-xs ${
                  darkMode ? 'text-white/40' : 'text-gray-500'
                }`}>
                  <i className={`${
                    selectedData.pricePerSqm >= prevData.pricePerSqm
                      ? 'ri-arrow-up-line text-emerald-500'
                      : 'ri-arrow-down-line text-rose-500'
                  }`}></i>
                  <span>
                    {Math.abs(
                      ((selectedData.pricePerSqm - prevData.pricePerSqm) / prevData.pricePerSqm) * 100
                    ).toFixed(1)}% vs {prevData.label}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ============================================== */}
      {/* LEGEND — shared                              */}
      {/* ============================================== */}
      <div className={`mt-4 flex items-center justify-center gap-4 text-xs ${darkMode ? 'text-white/40' : 'text-gray-400'}`}>
        <div className="flex items-center gap-1.5">
          <div className={`w-3 h-3 rounded ${darkMode ? 'bg-gradient-to-t from-white/15 to-white/5' : 'bg-gradient-to-t from-gray-200 to-gray-100'}`} />
          <span>Anos anteriores</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-gradient-to-t from-teal-600 to-emerald-500" />
          <span className={`font-semibold ${darkMode ? 'text-teal-300' : 'text-teal-700'}`}>{data[data.length - 1]?.label} (atual)</span>
        </div>
      </div>
    </div>
  );
}